import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Table, TableRow, TableHead, TableBody, TableCell, TableContainer, Collapse, IconButton, } from '@mui/material';
import { formatMoneyAmount, weightFormat, kgFobPriceToLbFobPrice, feePer1kgToFeePer1lb, } from '../../utils/format';
import { AccordionCollapseIcon, AccordionExpandIcon } from '../atoms/Icons';
var OrderPriceComponent = function (_a) {
    var orderPriceItemType = _a.orderPriceItemType, offers = _a.offers, orderPrice = _a.orderPrice, _b = _a.isUSRoaster, isUSRoaster = _b === void 0 ? false : _b, renderDiscountFields = _a.renderDiscountFields;
    var _c = useTranslation(), t = _c.t, i18n = _c.i18n;
    var moneyFormat = React.useCallback(function (value) { return formatMoneyAmount(value, orderPrice.currency); }, [orderPrice]);
    var quantityWithWeight = React.useCallback(function (item) {
        var _a = item, offerId = _a.offerId, quantity = _a.quantity;
        var offer = offers.find(function (_a) {
            var id = _a.id;
            return id === offerId.toString();
        });
        if (!offer) {
            return quantity;
        }
        var weight = orderPriceItemType === 'TypicaStockItem'
            ? weightFormat(parseFloat(offer.bagSize) * quantity)
            : weightFormat(parseFloat(offer.minOrder) * quantity);
        return "".concat(quantity, " (").concat(weight, ")");
    }, [offers, orderPriceItemType]);
    var COLUMNS = React.useMemo(function () {
        var subTotalLabel = orderPrice.isJpCalculation
            ? "".concat(t('params.orderPriceItem.subTotal'), " *1")
            : t('params.orderPriceItem.subTotal');
        return [
            {
                field: 'offerLotNumber',
                className: 'lot-number',
                label: t('params.offer.lotNumber'),
                headWithData: true,
            },
            {
                field: 'offerFobPrice',
                label: t("params.offer.".concat(isUSRoaster ? 'fobPriceUsdPerLb' : 'fobPrice')),
                align: 'right',
                get: function (item) {
                    return isUSRoaster
                        ? item.bidFobPriceUsdPerLb ||
                            kgFobPriceToLbFobPrice(Number(item.offerFobPrice))
                        : item.bidFobPrice || item.offerFobPrice;
                },
            },
            {
                field: 'quantityWithWeight',
                label: t('quantityWithWeight'),
                get: function (item) { return quantityWithWeight(item); },
            },
            {
                field: 'subTotal',
                label: subTotalLabel,
                align: 'right',
                format: moneyFormat,
            },
            {
                field: 'totalFee',
                label: t('params.orderPriceItem.totalFee'),
                align: 'right',
                format: moneyFormat,
            },
        ];
    }, [t, moneyFormat, isUSRoaster, quantityWithWeight, orderPrice]);
    var ITEMS = React.useMemo(function () {
        var shippingFeeLabel = t('params.orderPriceItem.shippingFee');
        if (orderPrice.isJpCalculation)
            shippingFeeLabel = "".concat(shippingFeeLabel, " *2");
        var typicaHandlingFeeLabel = t('params.orderPriceItem.typicaHandlingFee');
        if (orderPrice.isJpCalculation)
            typicaHandlingFeeLabel = "".concat(typicaHandlingFeeLabel, " *3");
        var paymentTimingHandlingFeePositiveValueLabel = t('labels.paymentTimingHandlingFee.positiveValue');
        if (orderPrice.isJpCalculation)
            paymentTimingHandlingFeePositiveValueLabel = "".concat(paymentTimingHandlingFeePositiveValueLabel, " *3");
        return [
            {
                field: 'shippingFee',
                label: shippingFeeLabel,
                format: moneyFormat,
            },
            {
                field: 'miscFeeForJp',
                label: t('labels.miscFeeForJp'),
                get: function (item) {
                    return (parseFloat(item.miscFee) +
                        parseFloat(item.taxedMiscFee) +
                        parseFloat(item.importTax));
                },
                format: moneyFormat,
                isHide: function () { return !orderPrice.isJpCalculation; },
            },
            {
                field: 'miscFee',
                label: t('params.orderPriceItem.miscFee'),
                format: moneyFormat,
                isHide: function () { return orderPrice.isJpCalculation; },
            },
            {
                field: 'taxedMiscFee',
                label: t('params.orderPriceItem.taxedMiscFee'),
                format: moneyFormat,
                isHide: function () { return orderPrice.isJpCalculation; },
            },
            {
                field: 'importTax',
                label: t('params.orderPriceItem.importTax'),
                format: moneyFormat,
                isHide: function () { return orderPrice.isJpCalculation; },
            },
            {
                field: 'typicaHandlingFee',
                label: typicaHandlingFeeLabel,
                format: moneyFormat,
            },
            {
                field: 'paymentTimingHandlingFee.negativeValue',
                label: t('labels.paymentTimingHandlingFee.negativeValue'),
                get: function (item) { return item.paymentTimingHandlingFee; },
                format: function (value) {
                    if (Number(value) >= 0)
                        return '-';
                    return moneyFormat(value);
                },
            },
            {
                field: 'paymentTimingHandlingFee.positiveValue',
                label: paymentTimingHandlingFeePositiveValueLabel,
                get: function (item) { return item.paymentTimingHandlingFee; },
                format: function (value) {
                    if (Number(value) <= 0)
                        return '-';
                    return moneyFormat(value);
                },
            },
            {
                field: 'consumptionTaxTotal',
                label: "".concat(orderPrice.taxName),
                format: moneyFormat,
            },
            {
                field: 'serviceFee',
                label: t('params.orderPriceItem.serviceFee'),
                format: moneyFormat,
            },
            {
                field: 'campaignDiscount',
                label: t('labels.newRoasterCampaignDiscount'),
                format: function (value) {
                    if (Number(value) <= 0)
                        return '-';
                    return moneyFormat('-' + value);
                },
            },
            {
                field: 'totalFeeDiscount',
                label: t('params.orderPriceItem.totalFeeDiscount'),
                format: function (value) {
                    var num = Number(value);
                    return moneyFormat(-num);
                },
                isHide: function (value) {
                    var num = Number(value);
                    return isNaN(num) || num === 0;
                },
            },
            {
                field: 'feePer1kg',
                label: t("params.orderPriceItem.".concat(isUSRoaster ? 'feePer1lb' : 'feePer1kg')),
                format: function (value) {
                    return moneyFormat(isUSRoaster ? feePer1kgToFeePer1lb(Number(value)) : value);
                },
            },
        ];
    }, [
        t,
        orderPrice.taxName,
        moneyFormat,
        isUSRoaster,
        orderPrice.isJpCalculation,
    ]);
    var enabledItems = React.useMemo(function () {
        var serviceFeeIncluded = orderPrice.items.find(function (item) { return Number(item.serviceFee) > 0; });
        var positivePaymentTimingHandlingFeeIncluded = orderPrice.items.find(function (item) { return Number(item.paymentTimingHandlingFee) > 0; });
        var negativePaymentTimingHandlingFeeIncluded = orderPrice.items.find(function (item) { return Number(item.paymentTimingHandlingFee) < 0; });
        var campaignDiscountIncluded = orderPrice.items.find(function (item) { return Number(item.campaignDiscount) > 0; });
        var items = ITEMS;
        if (!serviceFeeIncluded) {
            items = items.filter(function (item) { return item.field != 'serviceFee'; });
        }
        if (!positivePaymentTimingHandlingFeeIncluded) {
            items = items.filter(function (item) { return item.field != 'paymentTimingHandlingFee.positiveValue'; });
        }
        if (!negativePaymentTimingHandlingFeeIncluded) {
            items = items.filter(function (item) { return item.field != 'paymentTimingHandlingFee.negativeValue'; });
        }
        if (!campaignDiscountIncluded) {
            items = items.filter(function (item) { return item.field != 'campaignDiscount'; });
        }
        return items;
    }, [ITEMS, orderPrice]);
    var taxedFootNote = React.useMemo(function () {
        if (orderPrice.isJpCalculation) {
            return {
                mark: '',
                note: t('labels.taxNodeForJpCalculation'),
            };
        }
        var mark = i18n.language === 'ja' ? '(※1)' : '*1';
        return {
            mark: mark,
            note: "".concat(mark, " ").concat(t('labels.taxed')),
        };
    }, [i18n, t, orderPrice]);
    var _d = useState(true), open = _d[0], setOpen = _d[1];
    return (React.createElement("div", null,
        React.createElement(TableContainer, null,
            React.createElement(Table, null,
                React.createElement(TableHead, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableCell, { sx: { width: '24px', minWidth: '24px' } }),
                        COLUMNS.map(function (_a) {
                            var field = _a.field, label = _a.label, align = _a.align;
                            return (React.createElement(TableCell, { key: field, sx: {
                                    padding: {
                                        xs: '0 8px',
                                        md: '0 16px',
                                    },
                                    textAlign: align ? align : null,
                                    width: '100%',
                                    typography: function (theme) { return theme.customTypography.body4; },
                                } }, label));
                        }),
                        React.createElement(TableCell, { sx: { padding: '0 4px' } }))),
                React.createElement(TableBody, null, orderPrice.items.map(function (item) {
                    return (React.createElement(React.Fragment, { key: item.offerId },
                        React.createElement(TableRow, null,
                            React.createElement(TableCell, { sx: { padding: '8px 0' } },
                                React.createElement(IconButton, { color: open ? 'inherit' : 'default', onClick: function () { return setOpen(!open); }, sx: {
                                        ml: 1.5,
                                        padding: '3px',
                                    } }, open ? (React.createElement(AccordionCollapseIcon, { sx: { height: '18px', width: '18px' } })) : (React.createElement(AccordionExpandIcon, { sx: { height: '18px', width: '18px' } })))),
                            COLUMNS.map(function (column) {
                                var field = column.field, align = column.align, _a = column.get, get = _a === void 0 ? defaultValueGetter : _a, _b = column.format, format = _b === void 0 ? defaultFormatter : _b, isHide = column.isHide;
                                var rawValue = get(item, column);
                                if (isHide && isHide(rawValue))
                                    return null;
                                var formattedValue = format(rawValue);
                                var value = isTaxedFee(item, column.field, orderPrice.isJpCalculation) && taxedFootNote.mark
                                    ? "".concat(formattedValue, " ").concat(taxedFootNote.mark)
                                    : formattedValue;
                                return (React.createElement(TableCell, { key: field, sx: {
                                        padding: {
                                            xs: '8px',
                                            md: '16px',
                                        },
                                        textAlign: align ? align : null,
                                        typography: function (theme) { return theme.customTypography.body4; },
                                    } }, value));
                            }),
                            React.createElement(TableCell, { sx: { padding: '0 4px' } })),
                        React.createElement(TableRow, null,
                            React.createElement(TableCell, { sx: { padding: 0 }, colSpan: 12 },
                                React.createElement(Collapse, { sx: {
                                        pl: { xs: 6, md: 7 },
                                        pr: { xs: 2, md: 3 },
                                    }, in: open, unmountOnExit: true },
                                    React.createElement(Stack, { spacing: 1, sx: { pb: 2 } },
                                        enabledItems.map(function (column) {
                                            var _a = column.className, className = _a === void 0 ? '' : _a, field = column.field, label = column.label, _b = column.get, get = _b === void 0 ? defaultValueGetter : _b, _c = column.format, format = _c === void 0 ? defaultFormatter : _c, isHide = column.isHide, headWithData = column.headWithData;
                                            var rawValue = get(item, column);
                                            if (isHide && isHide(rawValue))
                                                return null;
                                            var formattedValue = format(rawValue);
                                            var value = isTaxedFee(item, column.field, orderPrice.isJpCalculation) && taxedFootNote.mark
                                                ? "".concat(formattedValue, " ").concat(taxedFootNote.mark)
                                                : formattedValue;
                                            return (React.createElement(Stack, { key: field, direction: "row", justifyContent: "space-between", sx: {
                                                    typography: function (theme) {
                                                        return theme.customTypography.body4;
                                                    },
                                                } },
                                                React.createElement("dt", { className: className }, headWithData ? "".concat(label, ": ").concat(value) : label),
                                                React.createElement("dd", { className: className }, value)));
                                        }),
                                        renderDiscountFields &&
                                            renderDiscountFields(item.offerId)))))));
                })))),
        React.createElement(Box, { sx: {
                padding: {
                    xs: '0 16px 16px 48px',
                    md: '0 16px 16px 56px',
                },
                typography: function (theme) { return theme.customTypography.body4; },
            } }, taxedFootNote.note)));
};
export default OrderPriceComponent;
var defaultValueGetter = function (item, column) {
    return item[column.field];
};
var defaultFormatter = function (value) { return String(value); };
var isTaxedFee = function (item, field, isJpCalculation) {
    if (isJpCalculation) {
        if (['subTotal', 'shippingFee', 'miscFeeForJp'].includes(field)) {
            return true;
        }
        else {
            return false;
        }
    }
    switch (item.taxTarget) {
        case 'all':
            return [
                'subTotal',
                'shippingFee',
                'miscFee',
                'taxedMiscFee',
                'importTax',
                'typicaHandlingFee',
                'paymentTimingHandlingFee.negativeValue',
                'paymentTimingHandlingFee.positiveValue',
                'campaignDiscount',
            ].includes(field);
        case 'none':
            return false;
        case 'taxed_misc_fee_only':
            return field === 'taxedMiscFee';
        case 'taxed_misc_fee_typica_handling_fee':
            return field === 'taxedMiscFee' || field === 'typicaHandlingFee';
        case 'typica_handling_fee_only':
            return field === 'typicaHandlingFee';
        default:
            return false;
    }
};
